import React, { createContext, useContext, useMemo, useRef, useState } from 'react'
import { ACCOUNT_OPTIONS, SOURCE_KNOWN, FUND_ACCOUNTS, isFund, API_BASE, authHeaders } from './api'
import axios from 'axios'

const LedgerCtx = createContext(null)
export const useLedger = () => useContext(LedgerCtx)

export default function LedgerProvider({ children }) {
  const [filters, setFilters] = useState({ userId: '', account: '', source: '', dateFrom: '', dateTo: '', limit: 50 })
  const [sort, setSort] = useState({ field: 'createdAt', dir: 'desc' })
  const [sortWallet, setSortWallet] = useState({ field: 'createdAt', dir: 'desc' })
  const [sortPayout, setSortPayout] = useState({ field: 'createdAt', dir: 'desc' })
  const txnUserCache = useRef(new Map())

  const accountOptions = ACCOUNT_OPTIONS
  const [dynamicSources, setDynamicSources] = useState(new Set(SOURCE_KNOWN))
  const sourceOptions = useMemo(() => ['', ...Array.from(dynamicSources)], [dynamicSources])

  function addSourcesFromRows(rows) {
    const s = new Set(dynamicSources)
    rows.forEach(r => { if (r?.source) s.add(String(r.source)) })
    setDynamicSources(s)
  }

  // ------- export helpers shared -------
  const fileStamp = () => {
    const d = new Date(), pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`
  }
  function toPlainRows(rows) {
    return rows.map(r => ({
      txn_id: r.txn_id,
      createdAt: r.createdAt,
      side: r.side,
      account: r.account,
      user: r.displayUserId ?? r.userId ?? '-',
      amount: Number(r.amount || 0),
      currency: r.currency || 'EUR',
      source: r.source,
      description: r.description || ''
    }))
  }
  function buildCSV(rows) {
    const cols = ['txn_id','createdAt','side','account','user','amount','currency','source','description']
    const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s }
    const lines = [cols.join(';')]
    for (const r of rows) lines.push(cols.map(c => esc(r[c])).join(';'))
    return '\uFEFF' + lines.join('\n')
  }
  async function fetchWalletTxnIdsForUser(userId, { dateFrom, dateTo, source }) {
    const ids = new Set()
    const limit = 500
    let offset = 0
    for (let i = 0; i < 400; i++) {
      const p = new URLSearchParams()
      p.set('account', 'USER_WALLET')
      p.set('userId', String(userId))
      if (dateFrom) p.set('dateFrom', dateFrom)
      if (dateTo) p.set('dateTo', dateTo)
      if (source) p.set('source', source)
      p.set('limit', String(limit))
      p.set('offset', String(offset))
      const res = await axios.get(`${API_BASE}/api/ledger?${p.toString()}`, { headers: authHeaders() })
      const arr = Array.isArray(res.data) ? res.data : []
      for (const r of arr) if (r.txn_id) ids.add(r.txn_id)
      if (arr.length < limit) break
      offset += limit
    }
    return ids
  }
  async function fetchAllFilteredRows(f) {
    const rows = []
    const limit = 500
    let offset = 0
    const isFundAndUser = f.account !== 'VAT_FUND' && FUND_ACCOUNTS.includes(f.account || '') && String(f.userId || '').trim() !== ''
    let walletTxnSet = null
    if (isFundAndUser) {
      walletTxnSet = await fetchWalletTxnIdsForUser(f.userId, { dateFrom: f.dateFrom, dateTo: f.dateTo, source: f.source })
    }
    for (let page = 0; page < 400; page++) {
      const p = new URLSearchParams()
      Object.entries(f).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return
        if (k === 'limit') return
        if (k === 'userId' && isFundAndUser) return
        p.set(k, String(v))
      })
      p.set('limit', String(limit))
      p.set('offset', String(offset))
      const res = await axios.get(`${API_BASE}/api/ledger?${p.toString()}`, { headers: authHeaders() })
      let arr = Array.isArray(res.data) ? res.data : []
      if (walletTxnSet) {
        arr = arr.filter(r => r.txn_id && walletTxnSet.has(r.txn_id)).map(r => ({ ...r, displayUserId: f.userId }))
      }
      rows.push(...arr)
      if (arr.length < limit) break
      offset += limit
    }
    return rows
  }
  async function exportCSV(filtered) {
    const all = await fetchAllFilteredRows(filtered)
    const csv = buildCSV(toPlainRows(all))
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `ledger-${fileStamp()}.csv`
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 0)
  }
  async function exportXLSX(filtered) {
    try {
      const XLSX = await import('xlsx')
      const all = await fetchAllFilteredRows(filtered)
      const ws = XLSX.utils.json_to_sheet(toPlainRows(all))
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Ledger')
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `ledger-${fileStamp()}.xlsx`
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 0)
    } catch {
      await exportCSV(filtered)
      alert('Knižnica "xlsx" nie je dostupná – exportujem CSV.')
    }
  }
  async function exportPDF(filtered) {
    try {
      const jsPDF = (await import('jspdf')).default
      const autoTable = (await import('jspdf-autotable')).default
      const all = await fetchAllFilteredRows(filtered)
      const rows = toPlainRows(all)
      const doc = new jsPDF({ orientation: 'landscape' })
      doc.setFontSize(12); doc.text('Ledger – Všetky transakcie (aktuálne filtre)', 14, 12)
      autoTable(doc, {
        startY: 16,
        head: [['Txn ID','Created','Side','Account','User','Amount','Currency','Source','Description']],
        body: rows.map(r => [r.txn_id, r.createdAt, r.side, r.account, r.user, String(r.amount), r.currency, r.source, r.description]),
        styles: { fontSize: 8 }
      })
      doc.save(`ledger-${fileStamp()}.pdf`)
    } catch {
      const all = await fetchAllFilteredRows(filtered)
      const rows = toPlainRows(all)
      const w = window.open('', '_blank')
      const css = `<style>body{font-family:Arial;padding:12px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left}th{background:#eee}</style>`
      const head = ['Txn ID','Created','Side','Account','User','Amount','Currency','Source','Description']
      const rowsHtml = rows.map(r =>
        `<tr><td>${r.txn_id}</td><td>${r.createdAt}</td><td>${r.side}</td><td>${r.account}</td><td>${r.user}</td><td>${r.amount}</td><td>${r.currency}</td><td>${r.source}</td><td>${r.description}</td></tr>`
      ).join('')
      w.document.write(`${css}<h3>Ledger – Všetky transakcie (aktuálne filtre)</h3><table><thead><tr>${head.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table>`)
      w.document.close(); w.focus(); w.print()
    }
  }

  async function augmentUserIds(list) {
    if (!Array.isArray(list) || list.length === 0) return []
    const map = new Map()
    list.forEach(r => {
      if (r.account === 'USER_WALLET' && r.userId != null && r.txn_id) {
        map.set(r.txn_id, r.userId)
      }
    })
    for (const [k, v] of txnUserCache.current.entries()) {
      if (!map.has(k)) map.set(k, v)
    }
    const indexByTxn = new Map()
    list.forEach(r => { if (r.txn_id) indexByTxn.set(r.txn_id, r) })
    const missing = Array.from(new Set(
      list.filter(r => r.account !== 'VAT_FUND' && r.txn_id && !map.has(r.txn_id)).map(r => r.txn_id)
    )).slice(0, 40)

    const parseUserFromDesc = (desc) => {
      if (!desc) return null
      const m = String(desc).match(/používateľa\s+(\d+)/i)
      return m ? Number(m[1]) : null
    }
    const fetchTxnUser = async (txn, row) => {
      try {
        const u1 = new URLSearchParams()
        u1.set('txn_id', txn); u1.set('account', 'USER_WALLET'); u1.set('limit', '50')
        const r1 = await axios.get(`${API_BASE}/api/ledger?${u1.toString()}`, { headers: authHeaders() })
        const arr1 = Array.isArray(r1.data) ? r1.data : []
        const m1 = arr1.find(x => (x.txn_id === txn || x.txnId === txn) && x.account === 'USER_WALLET' && x.userId != null)
        if (m1) return m1.userId
      } catch {}
      if (row?.source?.includes('REFERRAL')) {
        const parsed = parseUserFromDesc(row.description)
        if (parsed != null) return parsed
      }
      try {
        const t = row?.createdAt ? new Date(row.createdAt) : null
        const dateFrom = t ? new Date(t.getTime() - 1000*60*60*24*2) : null
        const dateTo   = t ? new Date(t.getTime() + 1000*60*60*24*2) : null
        const qp = new URLSearchParams()
        qp.set('account', 'USER_WALLET')
        if (row?.source) qp.set('source', row.source)
        if (dateFrom) qp.set('dateFrom', dateFrom.toISOString().slice(0, 10))
        if (dateTo) qp.set('dateTo', dateTo.toISOString().slice(0, 10))
        qp.set('limit', '500')
        const r2 = await axios.get(`${API_BASE}/api/ledger?${qp.toString()}`, { headers: authHeaders() })
        const arr2 = Array.isArray(r2.data) ? r2.data : []
        const absAmt = Math.abs(Number(row?.amount || 0))
        const candidates = arr2.filter(x => Math.abs(Number(x.amount || 0)) === absAmt)
        if (candidates.length > 0) {
          const base = t ? t.getTime() : 0
          candidates.sort((a, b) =>
            Math.abs(new Date(a.createdAt || 0).getTime() - base) -
            Math.abs(new Date(b.createdAt || 0).getTime() - base)
          )
          const c = candidates[0]
          if (c?.userId != null) return c.userId
        }
      } catch {}
      return null
    }
    await Promise.all(missing.map(async (txn) => {
      const row = indexByTxn.get(txn)
      const uid = await fetchTxnUser(txn, row)
      if (uid != null) {
        map.set(txn, uid)
        txnUserCache.current.set(txn, uid)
      }
    }))

    return list.map(r => {
      const pairedUid = r.txn_id ? map.get(r.txn_id) : null
      const isWallet = r.account === 'USER_WALLET'
      const isFundAcc = isFund(r.account)
      const parsedFromDesc = (!pairedUid && r?.source?.includes('REFERRAL')) ? (r.description ? (r.description.match(/používateľa\s+(\d+)/i)?.[1] ?? null) : null) : null
            const displayUserId = r.account === 'VAT_FUND' ? (r.userId ?? '-') : isWallet ? (r.userId ?? '-') : isFundAcc ? (pairedUid ?? parsedFromDesc ?? '-') : (r.userId ?? pairedUid ?? parsedFromDesc ?? '-')
      return { ...r, displayUserId }
    })
  }

  const value = {
    filters, setFilters,
    sort, setSort,
    sortWallet, setSortWallet,
    sortPayout, setSortPayout,
    accountOptions, sourceOptions, addSourcesFromRows,
    exportCSV, exportXLSX, exportPDF,
    augmentUserIds
  }
  return <LedgerCtx.Provider value={value}>{children}</LedgerCtx.Provider>
}
